import React, { useState, useEffect } from 'react';
import { Download, FileText, CheckCircle, XCircle, Award, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const MarksTabulation = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ 
    exam_id: '', 
    course_id: '', 
    semester_id: '',
    subject_id: '' 
  });
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [tabulationData, setTabulationData] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchSubjects();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ ...filters, course_id: courseId, semester_id: '', exam_id: '', subject_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setTabulationData(null);
  };

  const generateTabulation = async () => {
    if (!filters.exam_id || !filters.course_id || !filters.semester_id) {
      alert('Please select Exam, Course and Semester');
      return;
    }

    setLoading(true);
    try {
      // Get exam details
      const examResponse = await axios.get(
        `${API_URL}/api/examination/exams/${filters.exam_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const examData = examResponse.data;

      // Get students
      const studentsResponse = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsData = Array.isArray(studentsResponse.data) ? studentsResponse.data : (studentsResponse.data?.data || []);
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );

      // Get subjects for this course/semester
      let semesterSubjects = subjects.filter(
        sub => sub.course_id === filters.course_id && sub.semester_id === filters.semester_id
      );
      
      // If specific subject selected, show only that subject
      if (filters.subject_id) {
        semesterSubjects = semesterSubjects.filter(sub => sub.id === filters.subject_id);
      }

      // For each student, get marks and attendance for all subjects
      const studentData = [];
      
      for (const student of filteredStudents) {
        const subjectMarks = {};
        let totalMarks = 0;
        let totalMaxMarks = 0;
        let subjectsCount = 0;

        for (const subject of semesterSubjects) {
          // Get marks
          const marksResponse = await axios.get(
            `${API_URL}/api/examination/marks`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                exam_id: filters.exam_id,
                subject_id: subject.id,
                student_id: student.id
              }
            }
          );

          // Get attendance
          const attendanceResponse = await axios.get(
            `${API_URL}/api/examination/attendance/get`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                exam_id: filters.exam_id,
                subject_id: subject.id
              }
            }
          );

          let attendance = 'Not Marked';
          if (attendanceResponse.data.saved) {
            const studentAttendance = attendanceResponse.data.attendance.find(
              a => a.student_id === student.id
            );
            if (studentAttendance) {
              attendance = studentAttendance.status;
            }
          }

          if (marksResponse.data && marksResponse.data.length > 0) {
            const mark = marksResponse.data[0];
            subjectMarks[subject.id] = {
              marks: mark.marks_obtained,
              total: mark.total_marks,
              grade: mark.grade,
              attendance: attendance
            };
            totalMarks += mark.marks_obtained;
            totalMaxMarks += mark.total_marks;
            subjectsCount++;
          } else {
            subjectMarks[subject.id] = {
              marks: attendance === 'Absent' ? 'AB' : '-',
              total: examData.total_marks || 100,
              grade: '-',
              attendance: attendance
            };
            totalMaxMarks += (examData.total_marks || 100);
          }
        }

        const percentage = totalMaxMarks > 0 ? ((totalMarks / totalMaxMarks) * 100).toFixed(2) : 0;
        const overallGrade = calculateOverallGrade(percentage);

        studentData.push({
          student: student,
          subjectMarks: subjectMarks,
          totalMarks: totalMarks,
          totalMaxMarks: totalMaxMarks,
          percentage: percentage,
          grade: overallGrade
        });
      }

      // Sort by percentage (descending)
      studentData.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));

      setTabulationData({
        exam: examData,
        subjects: semesterSubjects,
        studentData: studentData
      });

    } catch (error) {
      console.error('Error generating tabulation:', error);
      alert('Failed to generate tabulation: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const exportToExcel = () => {
    alert('Excel export functionality will be implemented');
  };

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marks Tabulation</h1>
          <p className="text-sm text-gray-500 mt-1">View comprehensive marks tabulation sheet</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => navigate('/examination/marks-entry')}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Edit className="h-4 w-4" />
            Go to Marks Entry
          </button>
          {tabulationData && (
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.semester_id} 
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject (Optional)</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.subject_id || ''} 
              onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'All Subjects' : 'Select course first'}</option>
              {subjects.filter(s => s.course_id === filters.course_id && s.semester_id === filters.semester_id).map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name} ({sub.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.exam_id} 
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Exam' : 'Select course first'}</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name} - {exam.exam_type}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateTabulation}
              disabled={!filters.exam_id || !filters.course_id || !filters.semester_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Generating...' : 'Generate Tabulation'}
            </button>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>💡 How to enter marks:</strong> Click the "Go to Marks Entry" button above → Select Exam → Select Subject → Load Students → Enter marks → Save
          </p>
        </div>
      </div>

      {tabulationData ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 bg-blue-50 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">{tabulationData.exam.name}</h3>
                <p className="text-sm text-blue-700">
                  {tabulationData.exam.start_date} to {tabulationData.exam.end_date} | 
                  Total Students: {tabulationData.studentData.length}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-700">
                  Pass Rate: {((tabulationData.studentData.filter(s => s.percentage >= 40).length / tabulationData.studentData.length) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  {tabulationData.subjects.map(subject => (
                    <th key={subject.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" title={subject.name}>
                      {subject.code}
                      <br />
                      <span className="text-[10px] font-normal text-gray-400 normal-case">{subject.name}</span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">%</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Result</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tabulationData.studentData.map((data, index) => {
                  const isPassing = data.percentage >= 40;
                  return (
                    <tr key={data.student.id} className={isPassing ? '' : 'bg-red-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {index + 1}
                        {index === 0 && <Award className="inline h-4 w-4 text-yellow-500 ml-1" />}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {data.student.admission_number || data.student.roll_number || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{data.student.full_name}</td>
                      {tabulationData.subjects.map(subject => {
                        const subMark = data.subjectMarks[subject.id];
                        return (
                          <td key={subject.id} className="px-4 py-3 text-center text-sm">
                            <div>
                              <span className={subMark.marks === 'AB' ? 'text-red-600 font-semibold' : ''}>
                                {subMark.marks}
                              </span>
                              {subMark.marks !== 'AB' && subMark.marks !== '-' && (
                                <span className="text-gray-400">/{subMark.total}</span>
                              )}
                            </div>
                            {subMark.attendance === 'Absent' && (
                              <span className="text-xs text-red-500">Absent</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center text-sm font-semibold">
                        {data.totalMarks}/{data.totalMaxMarks}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-semibold">
                        {data.percentage}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          data.grade === 'F' ? 'bg-red-100 text-red-800' :
                          data.grade === 'A+' || data.grade === 'A' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {data.grade}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isPassing ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select filters to generate marks tabulation sheet</p>
        </div>
      )}
    </div>
  );
};

export default MarksTabulation;
